import { Flex, Skeleton } from 'antd';
import { CSSProperties } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { utils } from '@desygner/ui-common-components';

import { TemplateProjectPresets, presets } from './TemplateProject.presets';
import {
  ResponsiveProps,
  TemplateProjectProps,
  ThumbnailDimensions,
} from './TemplateProject.props';

const appearFromTop = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-100%);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
  90% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(0);
  }
`;

const StyledCardContainer = styled.div<
  TemplateProjectProps & ResponsiveProps & { thumbnailDimensions: ThumbnailDimensions | null }
>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${({ thumbnailDimensions }) => `${utils.rem(thumbnailDimensions?.width || 0)}`};

  &:hover .topActions {
    opacity: 1;
    animation: ${appearFromTop} 0.5s ease forwards;
  }

  border-radius: ${({ theme }) => `${utils.rem(theme.Border.Radius.Medium)}`};

  ${({ preset, shape }) => {
    if (['project', 'asset'].includes(preset)) {
      return css`
        width: ${utils.rem(presets[preset].shape[shape].width)};
      `;
    }

    return css``;
  }};

  ${({ isMobile, preset }) => {
    //Add styles for mobile
    if (isMobile) {
      return css`
        width: ${utils.rem(presets[preset].mobileWidth)};
        height: ${utils.rem(presets[preset].mobileHeight)};
        min-width: ${utils.rem(presets[preset].minWidth)};
        min-height: ${utils.rem(presets[preset].minHeight)};
      `;
    }

    return css``;
  }};
`;

const StyledCardContainerBase = styled.div<TemplateProjectProps>`
  border-radius: ${({ theme }) => `${utils.rem(theme.Border.Radius.Medium)}`};
  ${({ preset, shape }) => {
    if (['project', 'asset'].includes(preset)) {
      return css`
        padding: ${({ theme }) => `${utils.rem(theme.Spacing.Size.XSmall)}`};
        width: ${utils.rem(presets[preset].shape[shape].width)};
        border: ${({ theme }) =>
            `${utils.rem(theme.Card.Border.Size.Small)} ${theme.Color.NeutralsFixed.Border}`}
          solid;
      `;
    }

    return css``;
  }};
  ${({ selected }) => {
    //Add styles when selected
    if (selected) {
      return css`
        border: ${({ theme }) =>
            `${utils.rem(theme.Card.Border.Size.Small)} ${theme.Color.System.Primary}`}
          solid;
      `;
    }

    return css``;
  }};
`;

const StyledCardIcon = styled.div<{
  isVideoPlayer?: boolean;
  isBadge?: boolean;
  isCardPages?: boolean;
  automationIcon?: boolean;
}>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => `${utils.rem(theme.Spacing.Size.Large)}`};
  height: ${({ theme }) => `${utils.rem(theme.Spacing.Size.Large)}`};
  opacity: ${({ automationIcon }) => (automationIcon ? 1 : 0)};
  z-index: ${({ theme }) => `${theme.ZIndex.ZIndex.Elevated}`};

  ${({ isVideoPlayer, isBadge, isCardPages }) => {
    if (isVideoPlayer || isCardPages) {
      return css`
        opacity: 1;
        align-items: flex-end;
      `;
    }

    if (isBadge) {
      return css`
        opacity: 1;
        align-items: flex-end;
        justify-content: flex-end;
      `;
    }

    return css``;
  }};
`;

const StyledCard = styled.div<
  {
    preset: TemplateProjectPresets;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  } & { thumbnailDimensions: ThumbnailDimensions | null }
>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  border-radius: ${({ theme }) => `${utils.rem(theme.Border.Radius.Medium)}`};

  height: ${({ thumbnailDimensions }) => `${utils.rem(thumbnailDimensions?.height || 0)}`};
  min-width: ${({ preset }) => `${utils.rem(presets[preset].minWidth)}`};
  min-height: ${({ preset }) => `${utils.rem(presets[preset].minHeight)}`};
  max-width: ${({ preset }) => `${utils.rem(presets[preset].maxWidth)}`};
  max-height: ${({ preset }) => `${utils.rem(presets[preset].maxHeight)}`};

  ${({ preset }) => {
    //Add styles for project
    if (['project', 'asset'].includes(preset)) {
      return css`
        border: ${({ theme }) =>
            `${utils.rem(theme.Card.Border.Size.Small)} ${theme.Color.NeutralsFixed.Border}`}
          solid;
      `;
    }

    return css``;
  }};

  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  &:hover .topActions {
    opacity: 1;
    animation: ${appearFromTop} 0.5s ease forwards;
  }
`;

const StyledHoverableCard = styled(StyledCard)`
  &:hover ${StyledCardIcon} {
    opacity: 1;
  }
`;

const StyledSwiperSlide = styled.div<{
  preset: TemplateProjectPresets;
}>`
  ${({ preset }) => {
    if (preset === 'template') {
      return css`
        height: '100%',
        width: '100%',
        max-width: ${utils.rem(presets[preset].maxWidth)}
        max-height: ${utils.rem(presets[preset].maxHeight)}
        min-width: ${utils.rem(presets[preset].minWidth)}
        min-height: ${utils.rem(presets[preset].minHeight)}
      `;
    }

    return css``;
  }};
`;

const StyledMediaSlider = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  & .swiper-slide {
    margin-right: 0px;
  }
`;

const StyledSlide = styled.div`
  width: 100%;
  height: 100%;

  border-radius: ${({ theme }) => {
    return `${utils.rem(theme.Border.Radius.Medium)}`;
  }};
`;

const StyledSlideImg = styled.img<
  { src: string } & { preset: TemplateProjectPresets } & ResponsiveProps
>`
  width: 100%;
  height: 100%;
  src: url(${({ src }) => src});
  object-fit: contain;

  ${({ isMobile, preset }) => {
    //Add styles for mobile
    if (isMobile && preset === 'template') {
      return css`
        object-fit: fill;
      `;
    }

    return css``;
  }};
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: absolute;
  top: 0;
  left: 0;
  overflow: hidden;
`;

const StyledCardRow = styled.div<{ preset: TemplateProjectPresets }>`
  display: flex;
  justify-content: flex-start;
  &:last-child {
    justify-content: space-between;
  }

  ${({ preset }) => {
    //Add styles for template
    if (preset === 'template') {
      return css`
        padding-top: ${({ theme }) => `${utils.rem(theme.Spacing.Size.XSmall)}`};
        padding-bottom: ${({ theme }) => `${utils.rem(theme.Spacing.Size.XSmall)}`};
        width: ${({ theme }) => `calc(100% -  ${utils.rem(theme.Spacing.Size.MediumSmall)})`};
      `;
    }

    if (['project', 'asset'].includes(preset)) {
      return css`
        width: 100%;
      `;
    }

    return css``;
  }};
`;

const StyledRightAlignedRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: ${({ theme }) => `${utils.rem(theme.Spacing.Size.XXSmall)}`};
`;

export const StyledSkeletonCard = styled(Flex)`
  ${({ theme }) => {
    const { Color, Animation } = theme.Components.Skeleton;

    return `
    flex-direction: column;
    gap: ${utils.rem(theme.Spacing.Size.XSmall)};
    width: 100%;
    .ant-skeleton > *:is(.ant-skeleton-input,.ant-skeleton-image) {
      background: linear-gradient(90deg,
        ${Color.Dark} ${Animation.Gradient.Start}%,
        ${Color.Light} ${Animation.Gradient.Center}%,
        ${Color.Dark} ${Animation.Gradient.End}%);
      background-size: ${Animation.Background}% 100%;
    }
  `;
  }}
`;

export const StyledSkeletonImage = styled(Skeleton.Image)<{
  isMobile?: boolean;
  preset: TemplateProjectPresets;
}>`
  ${({ theme, isMobile, preset }) => `
  border-radius: ${theme.Spacing.Size.XXSmall}}!important;
  width: ${utils.rem(
    !isMobile ? presets[preset].skeleton.desktop.width : presets[preset].skeleton.mobile.width
  )}!important;
  height: ${utils.rem(
    !isMobile ? presets[preset].skeleton.desktop.height : presets[preset].skeleton.mobile.height
  )}!important;

  &&& .ant-skeleton-image-svg {
        height: ${utils.rem(presets[preset].skeletonMockImage.height)};
        width: ${utils.rem(presets[preset].skeletonMockImage.width)};
      }
      
      &&& .ant-skeleton-image {
        background-color: ${theme.Color.Neutrals.Light}};
      }
      `}
`;

export const StyledSkeletonAvatar = styled(Skeleton.Avatar)`
  ${({ theme }) => `
    &&& .ant-skeleton-avatar {
      height: ${utils.rem(theme.Spacing.Size.MediumLarge)}; 
      width: ${utils.rem(theme.Spacing.Size.MediumLarge)};
      background-color: ${theme.Color.Neutrals.Light}};
    }
    `}
`;

const StyledSkeletonInput = styled(Skeleton.Input)<{
  preset: TemplateProjectPresets;
}>`
  ${({ theme, preset }) => `
    span {
      height: ${utils.rem(theme.Spacing.Size.MediumSmall)} !important;
      width: ${utils.rem(presets[preset].skeletonInput.width)} !important;
      background-color: ${theme.Color.Neutrals.Light} !important;
    };
  `}
`;

const SwiperStyle: CSSProperties = {
  width: '-webkit-fill-available',
  height: '-webkit-fill-available',
};

export const TemplateProjectStyles = {
  StyledCardContainer,
  StyledMediaSlider,
  StyledSlide,
  StyledSlideImg,
  StyledVideo,
  StyledCardRow,
  StyledRightAlignedRow,
  StyledCardIcon,
  StyledHoverableCard,
  StyledSwiperSlide,
  SwiperStyle,
  StyledSkeletonCard,
  StyledSkeletonImage,
  StyledSkeletonAvatar,
  StyledSkeletonInput,
  StyledCardContainerBase,
};
